import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { formatInteger } from '../../format';

/** One section of a page split by `Tabs`. */
export interface Tab {
  /** What the address says: lowercase, accent-free, hyphenated (`validacoes-e-regras`). */
  slug: string;
  label: string;
  /** How many things are behind the tab, shown after the label when given. */
  count?: number;
  /** The panel. A function is only called while its tab is the active one. */
  content: ReactNode | (() => ReactNode);
}

/**
 * A strip of named sections, one of them shown.
 *
 * The selected tab lives in the address and nowhere else -- `?aba=` by
 * default, read and written through `useSearchParams` the way the companies
 * list keeps its filters and `pagina` -- so a reload or a pasted link opens the
 * same section, and Voltar steps back through the sections somebody visited.
 * A missing or unknown value means the first tab, and is left as it is rather
 * than corrected: opening a page must not add or rewrite a history entry.
 * Changing tab touches this one parameter and keeps every other.
 *
 * A click, Enter or Space pushes a history entry; the arrow keys, Home and End
 * move focus and selection together and replace it, so walking along the strip
 * is one step back rather than one per tab passed on the way.
 *
 * Only the active panel is mounted, for the reason the modal only mounts its
 * body when open: a panel that is not shown does not fire its queries, and a
 * form in it starts fresh when its tab is opened again.
 *
 * The markup is the WAI-ARIA tabs pattern: a labelled `tablist`, `tab` buttons
 * with a roving tabindex, so Tab enters the strip on the selected tab and
 * leaves it at the panel, and a `tabpanel` labelled by its tab.
 */
export function Tabs({
  tabs,
  label,
  param = 'aba',
}: {
  /** In display order. The first is the default. */
  tabs: readonly Tab[];
  /** The tablist's accessible name. */
  label: string;
  /** The query parameter that holds the selected slug. */
  param?: string;
}) {
  const baseId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const buttons = useRef(new Map<string, HTMLButtonElement>());

  const requested = searchParams.get(param);
  const active = tabs.find((tab) => tab.slug === requested) ?? tabs[0];
  if (active === undefined) {
    return null;
  }
  const activeIndex = tabs.indexOf(active);

  const tabId = (slug: string) => `${baseId}-tab-${slug}`;
  const panelId = (slug: string) => `${baseId}-panel-${slug}`;

  const select = (slug: string, replace: boolean) => {
    if (slug === requested) {
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(param, slug);
        return next;
      },
      { replace },
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = tabs.length - 1;
    let index: number;
    switch (event.key) {
      case 'ArrowRight':
        index = activeIndex === last ? 0 : activeIndex + 1;
        break;
      case 'ArrowLeft':
        index = activeIndex === 0 ? last : activeIndex - 1;
        break;
      case 'Home':
        index = 0;
        break;
      case 'End':
        index = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = tabs[index];
    if (target === undefined) {
      return;
    }
    buttons.current.get(target.slug)?.focus();
    if (index !== activeIndex) {
      select(target.slug, true);
    }
  };

  return (
    <div className="tabs">
      <div className="tabs__strip" role="tablist" aria-label={label}>
        {tabs.map((tab) => {
          const selected = tab === active;
          return (
            <button
              key={tab.slug}
              ref={(element) => {
                if (element) {
                  buttons.current.set(tab.slug, element);
                } else {
                  buttons.current.delete(tab.slug);
                }
              }}
              type="button"
              role="tab"
              id={tabId(tab.slug)}
              className={selected ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
              aria-selected={selected}
              aria-controls={panelId(tab.slug)}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(tab.slug, false)}
              onKeyDown={onKeyDown}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="tabs__count">{formatInteger(tab.count)}</span>
              )}
            </button>
          );
        })}
      </div>
      <div
        key={active.slug}
        className="tabs__panel"
        role="tabpanel"
        id={panelId(active.slug)}
        aria-labelledby={tabId(active.slug)}
        tabIndex={0}
      >
        {typeof active.content === 'function' ? active.content() : active.content}
      </div>
    </div>
  );
}
